import { BRAND } from '@palaccount/config';
import { FAVORITE_LIMIT } from '@palaccount/contracts';
import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { useAddFavorite, useFavorites, useRemoveFavorite } from '@/features/favorites/api';
import { cn } from '@/lib/utils';

/**
 * The label a favourite is stored under: the page's own document title, with
 * the brand suffix `pageTitle()` appends taken off — a document page sets its
 * number there (INV-H8), a list page its heading. When a screen has not set
 * one yet, the top bar's heading stands in.
 */
export function favoriteLabel(documentTitle: string, fallback: string): string {
  const suffix = ` · ${BRAND.titleSuffix}`;
  const own = documentTitle.endsWith(suffix) ? documentTitle.slice(0, -suffix.length).trim() : '';
  return own === '' ? fallback : own;
}

/**
 * G-01 / INV-H8 — the star beside the page title. Filled when the current
 * route is pinned; one click toggles. Icon-only, so it carries an accessible
 * name and a tooltip from one key (GEN-182 / NFR-37), and `aria-pressed` says
 * which way it stands without anybody reading the fill.
 */
export function FavoriteStar({ fallbackLabel }: { fallbackLabel: string }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { data } = useFavorites();
  const add = useAddFavorite();
  const remove = useRemoveFavorite();

  const current = data?.favorites.find((favorite) => favorite.route === pathname);
  const full = current === undefined && (data?.favorites.length ?? 0) >= FAVORITE_LIMIT;
  const name = current ? t('fav.remove') : t('fav.add');

  return (
    <button
      type="button"
      onClick={() => {
        if (current) remove.mutate(current.id);
        else add.mutate({ route: pathname, label: favoriteLabel(document.title, fallbackLabel) });
      }}
      // Until the list has arrived there is nothing to toggle against; at the
      // cap the tooltip says why rather than the click doing nothing.
      disabled={data === undefined || full || add.isPending || remove.isPending}
      aria-pressed={current !== undefined}
      aria-label={name}
      title={full ? t('fav.limit', { max: FAVORITE_LIMIT }) : name}
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-accent disabled:opacity-50',
        current ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      <Star className="size-4.5" fill={current ? 'currentColor' : 'none'} aria-hidden />
    </button>
  );
}
